import { supabase } from '../supabase';
import { fetchPartnerActivity } from './portal/partnerPortalApi';
import type {
  PartnerAuthCredentials,
  PartnerAuthResponse,
  PartnerPortalAccess,
  PartnerActivityLog
} from '../types';

// Nonostante il nome, questo servizio lavora sulle tabelle del portale collaboratore
// (`collaborator_portal_access` / `collaborator_portal_sessions`), non su quelle partner.
//
// Colonne leggibili dal browser: `password_hash` è escluso di proposito, la verifica avviene
// lato server con `portal_collaborator_login`. Vedi MD/security/SECURITY_AUDIT_2026-07-30_RLS.md
const ACCESS_COLUMNS = [
  'id', 'organization_id', 'external_partner_id', 'username', 'is_active', 'permissions',
  'last_login_at', 'login_attempts', 'locked_until', 'created_at', 'updated_at',
  'created_by', 'preferred_language'
].join(', ');

// Segnaposto usato alla creazione, prima che `portal_set_password` imposti quella vera.
const PASSWORD_PLACEHOLDER = '!non-impostata!';

/**
 * `collaborator_portal_access` è condivisa fra portale collaboratore e portale partner:
 * gli accessi partner hanno `permissions` come ARRAY (["view_services","request_quotes"]),
 * quelli collaboratore come OGGETTO ({"view_assigned_tasks": true, ...}) — è anche il
 * default della colonna a DB.
 *
 * Prima di questa modifica qualunque forma diversa da un array veniva sostituita con
 * `['view_services','request_quotes']`: un accesso puramente collaboratore che si autenticava
 * sul portale partner riceveva così i permessi partner completi, e le guard di rotta lo
 * lasciavano entrare nel catalogo con i prezzi di listino. Ora l'oggetto viene tradotto nelle
 * sue sole chiavi vere e non si inventa più nessun permesso: chi non ha `view_services`
 * non passa la guard.
 *
 * Resta aperto il lato server (portal_perm considera vero un permesso assente): il gating
 * qui è la metà frontend del difetto 4.
 */
function normalizePartnerPermissions(raw: unknown): string[] {
  let value = raw;

  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) {
    return value.filter((p): p is string => typeof p === 'string');
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, enabled]) => enabled === true)
      .map(([key]) => key);
  }

  return [];
}

interface CreatePartnerAccessData {
  partner_id: string;
  username: string;
  password: string;
  permissions?: string[];
  is_active?: boolean;
}

export class PartnerAuthService {
  /**
   * Create new partner portal access
   */
  static async createPartnerAccess(data: CreatePartnerAccessData): Promise<PartnerPortalAccess | null> {
    try {
      // Get current user and organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      // La password non viene mai cifrata nel browser: riga creata con un segnaposto (la
      // colonna è NOT NULL) e password vera impostata subito dopo con `portal_set_password`.
      const { data: accessData, error } = await supabase
        .from('collaborator_portal_access')
        .insert({
          organization_id: userProfile.organization_id,
          external_partner_id: data.partner_id,
          username: data.username,
          password_hash: PASSWORD_PLACEHOLDER,
          permissions: data.permissions || ['view_services', 'request_quotes'],
          is_active: data.is_active !== false,
          created_by: userData.user.id
        })
        .select(`
          ${ACCESS_COLUMNS},
          partner:external_partners(
            id, name, company, email
          )
        `)
        .single();

      if (error) throw error;

      const { error: passwordError } = await supabase.rpc('portal_set_password', {
        p_portal: 'collaborator',
        p_access_id: accessData.id,
        p_password: data.password
      });

      if (passwordError) {
        await supabase.from('collaborator_portal_access').delete().eq('id', accessData.id);
        throw passwordError;
      }

      // accessData non contiene password_hash: non è fra le colonne selezionate
      return accessData;
    } catch (error) {
      console.error('Error creating partner access:', error);
      return null;
    }
  }

  /**
   * Authenticate partner and create session (direct Supabase query)
   */
  static async authenticatePartner(
    credentials: PartnerAuthCredentials,
    ipAddress?: string,
    userAgent?: string
  ): Promise<PartnerAuthResponse> {
    try {
      // Autenticazione interamente lato server: blocco account, verifica password,
      // tentativi, creazione sessione. Restituisce l'accesso privo di `password_hash`.
      const { data, error } = await supabase.rpc('portal_collaborator_login', {
        p_username: credentials.username,
        p_password: credentials.password,
        p_ip: ipAddress ?? null,
        p_user_agent: userAgent ?? null
      });

      if (error) throw error;
      if (!data?.success) {
        return { success: false, error: data?.error || 'Credenziali non valide' };
      }

      const access = data.access;

      access.permissions = normalizePartnerPermissions(access.permissions);

      // Il login è già tracciato lato server da `portal_collaborator_login`, che scrive in
      // portal_activity_log. L'insert diretto in partner_activity_log che stava qui era una
      // scrittura come anon su una tabella con policy aperta: falsificabile da chiunque.

      return {
        success: true,
        access,
        session: data.session
      };
    } catch (error) {
      console.error('Error authenticating partner:', error);
      return { success: false, error: 'Autenticazione fallita' };
    }
  }

  /**
   * Validate partner session (direct Supabase query)
   */
  static async validateSession(sessionToken: string): Promise<PartnerPortalAccess | null> {
    try {
      // Validazione lato server: il browser non legge più la tabella delle sessioni.
      const { data, error } = await supabase.rpc('portal_collaborator_validate_session', {
        p_token: sessionToken
      });

      if (error || !data) {
        return null;
      }

      data.permissions = normalizePartnerPermissions(data.permissions);

      return data;
    } catch (error) {
      console.error('Error validating session:', error);
      return null;
    }
  }

  /**
   * Logout partner (invalidate session)
   */
  static async logoutPartner(sessionToken: string): Promise<boolean> {
    try {
      // Anche il logout è tracciato lato server da `portal_collaborator_logout`.
      const { error } = await supabase.rpc('portal_collaborator_logout', { p_token: sessionToken });
      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error logging out partner:', error);
      return false;
    }
  }

  /**
   * Update partner access permissions
   */
  static async updatePartnerPermissions(accessId: string, permissions: string[]): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      const { error } = await supabase
        .from('collaborator_portal_access')
        .update({
          permissions,
          updated_by: userData.user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', accessId);

      return !error;
    } catch (error) {
      console.error('Error updating partner permissions:', error);
      return false;
    }
  }

  /**
   * Disable partner access
   */
  static async disablePartnerAccess(accessId: string): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      // Disable access
      const { error: updateError } = await supabase
        .from('collaborator_portal_access')
        .update({
          is_active: false,
          updated_by: userData.user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', accessId);

      if (updateError) return false;

      // Invalidate all sessions for this access
      await supabase
        .from('collaborator_portal_sessions')
        .delete()
        .eq('collaborator_access_id', accessId);

      return true;
    } catch (error) {
      console.error('Error disabling partner access:', error);
      return false;
    }
  }

  /**
   * Enable partner access (specchia disablePartnerAccess: stessa tabella, stessa gestione
   * errori, solo `is_active: true`. Non tocca le sessioni: non ce ne sono da invalidare).
   */
  static async enablePartnerAccess(accessId: string): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      const { error } = await supabase
        .from('collaborator_portal_access')
        .update({
          is_active: true,
          updated_by: userData.user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', accessId);

      return !error;
    } catch (error) {
      console.error('Error enabling partner access:', error);
      return false;
    }
  }

  /**
   * Reset partner password
   */
  static async resetPartnerPassword(accessId: string, newPassword: string): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      // L'hash lo calcola il database (bcrypt). La funzione azzera i tentativi, toglie il
      // blocco e cancella le sessioni aperte.
      const { error } = await supabase.rpc('portal_set_password', {
        p_portal: 'collaborator',
        p_access_id: accessId,
        p_password: newPassword
      });

      if (error) return false;

      // Traccia chi ha fatto il reset, che la funzione non conosce
      await supabase
        .from('collaborator_portal_access')
        .update({ updated_by: userData.user.id, updated_at: new Date().toISOString() })
        .eq('id', accessId);

      return true;
    } catch (error) {
      console.error('Error resetting partner password:', error);
      return false;
    }
  }

  /**
   * Registro attività del partner, letto con il token di sessione.
   * La scrittura del log passa da `logPartnerActivity` (RPC), non più da un insert diretto.
   */
  static async getPartnerActivity(sessionToken: string, limit = 50): Promise<PartnerActivityLog[]> {
    try {
      return await fetchPartnerActivity(sessionToken, limit);
    } catch (error) {
      console.error('Error fetching partner activity:', error);
      return [];
    }
  }

  /**
   * Get all partner access records for organization
   */
  static async getPartnerAccessList(): Promise<PartnerPortalAccess[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return [];

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) return [];

      const { data, error } = await supabase
        .from('collaborator_portal_access')
        .select(`
          ${ACCESS_COLUMNS},
          partner:external_partners(
            id, name, company, email
          )
        `)
        .eq('organization_id', userProfile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // password_hash non è fra le colonne selezionate.
      // permissions su collaborator_portal_access è jsonb (oggetto {chiave: bool}):
      // senza normalizzazione in array, formData.permissions.includes() nel
      // PartnerPortalAccessManager lancia TypeError e fa crashare il drawer partner.
      return (data || []).map((row) => ({
        ...row,
        permissions: normalizePartnerPermissions(row.permissions),
      }));
    } catch (error) {
      console.error('Error fetching partner access list:', error);
      return [];
    }
  }
}